import React, { useContext, useState, useEffect } from 'react';
import { Box, Chip, Paper, IconButton, Tooltip } from '@mui/material';
import { Edit, Delete, SyncOutlined } from '@mui/icons-material';
import TableComponent, { IColumnProps } from '../../../components/TableComponent';
import { AppContext } from '../../../context/AppContextProvider';
import DeviceFormModal from './_components/DeviceFormModal';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';
import SyncActiveToast from '../../../components/SyncActiveToast';
import ProgressDialog from '../../../components/ProgressDialog';
import {
  useGetAllDevicesByLicenseAsync,
  useDeleteDeviceByIdAsync,
  useUpdateDeviceExternalControlIdAsync,
  useSyncDeviceModuleHistoryAsync,
} from '../../../services/Devices/hooks/useMutation';
import { useJobQuery } from '../../../services/Jobs/hooks/useQuery';
import { ISportsDevice, ISportsDeviceFormData } from './types';
import { IDeviceSimpleProps } from '../../../services/Devices/types';
import { useGetAllSportHallsByLicenseAsync } from '../../../services/Sports/Halls/hooks/useQuery';

const SportsDevicesPage: React.FC = () => {
  const appContext = useContext(AppContext);
  const showSnackbar = appContext?.showSnackbar || ((message: string, type?: any) => {});
  const setIsShowLoadingComponent = appContext?.setIsShowLoadingComponent || (() => {});

  const [devices, setDevices] = useState<ISportsDevice[]>([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<ISportsDevice | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<ISportsDevice | null>(null);

  const [syncJobId, setSyncJobId] = useState<number | null>(null);
  const [isSyncToastOpen, setIsSyncToastOpen] = useState(false);
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);

  const { mutateAsync: mutateGetAllDevices, isLoading: isLoadingDevices } =
    useGetAllDevicesByLicenseAsync();

  const { mutateAsync: mutateDeleteDevice, isLoading: isLoadingDelete } =
    useDeleteDeviceByIdAsync();

  const { mutateAsync: mutateUpdateDeviceExternalControlId, isLoading: isLoadingUpdate } =
    useUpdateDeviceExternalControlIdAsync();

  const { mutateAsync: mutateSyncModuleHistory } = useSyncDeviceModuleHistoryAsync();

  const { data: syncJob } = useJobQuery(syncJobId);

  const { data: sportHalls } = useGetAllSportHallsByLicenseAsync(
    { pageNumber: 1, pageSize: 999 },
    { search: '' }
  );

  const loadDevices = async () => {
    setIsShowLoadingComponent(true);
    try {
      const data = await mutateGetAllDevices({});
      const formattedDevices: ISportsDevice[] = (data || [])
        .map((d: IDeviceSimpleProps) => ({
          id: d.id,
          name: d.name || 'Dispositivo sem nome',
          externalControlId: (d as any).externalControlId || '',
          moduleIp: (d as any).moduleIp || '',
          ip: (d as any).ip || '',
          httpPort: (d as any).httpPort || (d as any).tcpPort || '',
          rtspPort: (d as any).rtspPort || '',
          user: (d as any).username || '',
          password: (d as any).password || '',
          sportHallId: (d as any).sportHallId,
          sportHallName: sportHalls?.find((h: any) => h.id === (d as any).sportHallId)?.name || '',
          isOnline: d.isOnline || false,
          createdAt: (d as any).createdAt || new Date().toISOString(),
        }));
      setDevices(formattedDevices);
    } catch (error) {
      showSnackbar('Erro ao carregar dispositivos.', 'error');
      console.error(error);
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [sportHalls]);

  const handleOpenEdit = (device: ISportsDevice) => {
    setSelectedDevice(device);
    setIsFormModalOpen(true);
  };

  const handleOpenDelete = (device: ISportsDevice) => {
    setDeviceToDelete(device);
    setIsDeleteModalOpen(true);
  };

  const handleSyncModuleHistory = async (device: ISportsDevice) => {
    if (!device.externalControlId || !device.moduleIp) {
      showSnackbar(
        'Configure o ID e o IP do módulo neste dispositivo antes de sincronizar o histórico.',
        'error',
      );
      return;
    }

    try {
      const job = await mutateSyncModuleHistory(device.id);
      setSyncJobId(job.jobId);
      setIsSyncToastOpen(true);
    } catch (error: any) {
      showSnackbar(error?.message || 'Erro ao sincronizar histórico do módulo.', 'error');
    }
  };

  const handleSave = async (data: any) => {
    if (!selectedDevice?.id) {
      throw new Error('Nenhum dispositivo selecionado para atualizar.');
    }

    setIsShowLoadingComponent(true);
    try {
      // Endpoint dedicado: o PUT geral exige name/model (non-nullable no backend) e dava 400
      // com esse payload parcial. Essa tela só edita o ID do módulo mesmo, então usa o PATCH
      // específico em vez do update completo do device.
      //
      // Não engolir o erro aqui: se a mutation falhar, isso precisa propagar pro
      // DeviceFormModal (que é quem decide se fecha o modal e mostra sucesso), senão o
      // vínculo falha silenciosamente e a tela some achando que salvou.
      await mutateUpdateDeviceExternalControlId({
        id: selectedDevice.id,
        externalControlId: data.externalControlId,
        moduleIp: data.moduleIp,
      });
      await loadDevices();
      setSelectedDevice(null);
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deviceToDelete) {
      setIsShowLoadingComponent(true);
      try {
        await mutateDeleteDevice(deviceToDelete.id);
        showSnackbar('Dispositivo excluído com sucesso!', 'success');
        await loadDevices();
      } catch (error: any) {
        showSnackbar(error?.message || 'Erro ao excluir dispositivo.', 'error');
      } finally {
        setIsShowLoadingComponent(false);
        setIsDeleteModalOpen(false);
        setDeviceToDelete(null);
      }
    }
  };

  const columns: IColumnProps[] = [
    {
      label: 'Nome',
      id: 'name',
      width: '15%',
    },
    {
      label: 'ID do Módulo',
      id: 'externalControlId',
      width: '20%',
      render: (val: any) => (
        <Chip
          size="small"
          label={val || 'Não vinculado'}
          color={val ? 'primary' : 'default'}
          variant={val ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      label: 'Tipo',
      id: 'deviceType',
      width: '10%',
      render: (val: any) => (
        <Chip
          size="small"
          label={val === 'CAMERA' ? 'Câmera' : 'Módulo'}
          color={val === 'CAMERA' ? 'primary' : 'secondary'}
          variant="filled"
        />
      ),
    },
    {
      label: 'Quadra',
      id: 'sportHallName',
      width: '15%',
      render: (val: any) => <>{val || 'Não vinculado'}</>,
    },
    {
      label: 'IP',
      id: 'ip',
      width: '12%',
      render: (val: any) => <>{val || '-'}</>,
    },
    {
      label: 'Status',
      id: 'isOnline',
      width: '10%',
      align: 'center',
      render: (val: any) => (
        <Chip
          size="small"
          label={val ? 'Online' : 'Offline'}
          color={val ? 'success' : 'error'}
          variant="filled"
        />
      ),
    },
    {
      id: 'id',
      label: 'Ações',
      align: 'center',
      width: '18%',
      render: (id: any) => {
        const device = devices.find((d) => d.id === id);
        if (!device) {
          return <Box display="flex" gap={0.5} justifyContent="center">-</Box>;
        }
        return (
          <Box display="flex" gap={0.5} justifyContent="center">
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={() => handleOpenEdit(device)}
                color="primary"
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                device.externalControlId && device.moduleIp
                  ? 'Sincronizar histórico do módulo'
                  : 'Configure o ID e o IP do módulo para sincronizar o histórico'
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleSyncModuleHistory(device)}
                  color="info"
                  disabled={!device.externalControlId || !device.moduleIp}
                >
                  <SyncOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton
                size="small"
                onClick={() => handleOpenDelete(device)}
                color="error"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const getInitialData = (): ISportsDeviceFormData | null => {
    if (!selectedDevice) return null;
    return {
      id: selectedDevice.id,
      name: selectedDevice.name,
      externalControlId: selectedDevice.externalControlId,
      moduleIp: selectedDevice.moduleIp || '',
      ip: selectedDevice.ip || '',
      httpPort: selectedDevice.httpPort || '',
      rtspPort: selectedDevice.rtspPort || '',
      user: selectedDevice.user || '',
      password: selectedDevice.password || '',
      sportHallId: selectedDevice.sportHallId || 0,
        };
  };

  return (
    <Box>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <TableComponent
          title="Dispositivos"
          columns={columns}
          rows={devices}
          isLoading={isLoadingDevices}
          totalCount={devices.length}
          emptyMessage="Nenhum dispositivo cadastrado."
        />
      </Paper>

      <DeviceFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedDevice(null);
        }}
        onSave={handleSave}
        initialData={getInitialData()}
        isLoading={isLoadingUpdate}
      />

      <ConfirmDeleteModalComponent
        isLoading={isLoadingDelete}
        isVisibleDeleteDialog={isDeleteModalOpen}
        setIsVisibleDeleteDialog={setIsDeleteModalOpen}
        title="Excluir Dispositivo"
        message={`Tem certeza que deseja excluir o dispositivo "${deviceToDelete?.name}"?`}
        handleCancelDeletion={() => {
          setIsDeleteModalOpen(false);
          setDeviceToDelete(null);
        }}
        handleConfirmDeletion={handleConfirmDelete}
      />

      {syncJob && (
        <>
          <SyncActiveToast
            open={isSyncToastOpen}
            personName="Sincronização do histórico"
            progress={syncJob.progress}
            statusMessage={syncJob.message ?? undefined}
            success={syncJob.status === 'Completed'}
            successLingerMs={5000}
            operation="update"
            doneMessageOverride={syncJob.message ?? undefined}
            onView={() => {
              setIsSyncDialogOpen(true);
              setIsSyncToastOpen(false);
            }}
            onClose={() => setIsSyncToastOpen(false)}
          />
          <ProgressDialog
            open={isSyncDialogOpen}
            onClose={() => {
              setIsSyncDialogOpen(false);
              setSyncJobId(null);
            }}
            onMinimize={() => {
              setIsSyncDialogOpen(false);
              setIsSyncToastOpen(true);
            }}
            progress={syncJob.progress}
            status={
              syncJob.status === 'Completed'
                ? 'success'
                : syncJob.status === 'Failed'
                  ? 'error'
                  : syncJob.status === 'Canceled'
                    ? 'canceled'
                    : 'loading'
            }
            statusMessage={syncJob.message ?? 'Aguardando...'}
            title="Sincronização do Histórico do Módulo"
            maxWidth="xs"
          />
        </>
      )}
    </Box>
  );
};

export default SportsDevicesPage;